import {
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  Component,
  Injector,
  OnInit,
  afterNextRender,
  inject,
} from '@angular/core';
import { AnnounceDirective } from '../../../directives/announce.directive';
import { getClaimKey } from '../../../functions/get-claim-key';
import { getUrlParams } from '../../../functions/get-url-params';
import { leaveLink } from '../../../functions/claim/leaveLink';
import { AutoDetail } from '../../../models/api/view/claim/details/auto/auto-detail';
import { GeneralLiabilityResponse } from '../../../models/api/view/claim/details/general-liability/general-liability-response';
import { Action } from '../../../models/core/action';
import { SettingsService } from '../../../services/settings/settings.service';
import { BaseComponent } from '../../base/base.component';
import { ChartConfig } from '../../core/chart/chart.component';
import { LinkLabelComponent } from '../../core/labels/link-label.component';
import { CardComponent } from '../../core/panels/card.component';
import { DeferPanelComponent } from '../../core/panels/defer-panel.component';
import { AiSummaryCardComponent } from './ai-summary-card.component';
import { CertificationCardComponent } from './certification-card.component';
import { AdClaimCardComponent } from './claim-card/ad-claim-card.component';
import { AuClaimCardComponent } from './claim-card/au-claim-card.component';
import { DsClaimCardComponent } from './claim-card/ds-claim-card.component';
import { GlClaimCardComponent } from './claim-card/gl-claim-card.component';
import { LaClaimCardComponent } from './claim-card/la-claim-card.component';
import { PrClaimCardComponent } from './claim-card/pr-claim-card.component';
import { WcClaimCardComponent } from './claim-card/wc-claim-card.component';
import { ClaimantCardComponent } from './claimant-card.component';
import { ContactCardComponent } from './contact-card.component';
import { FinancialCardComponent } from './financial-card.component';
import { LeaveBalanceCardComponent } from './leave-balance-card.component';
import { LeaveCalendarCardComponent } from './leave-calendar-card.component';
import { LossLocationCardComponent } from './loss-location-card.component';
import { NotesCardComponent } from './notes-card.component';
import { NotificationCardComponent } from './notification-card.component';
import { RelatedClaimsCardV2Component } from './related-claims-card-v2.component';
import { ReserveCardComponent } from './reserve-card.component';
import { TimelineCardComponent } from './timeline-card.component';

@Component({
  selector: 'app-claim-summary',
  standalone: true,
  imports: [
    CommonModule,
    CardComponent,
    CdkDropList,
    ClaimantCardComponent,
    FinancialCardComponent,
    ReserveCardComponent,
    ContactCardComponent,
    AiSummaryCardComponent,
    LossLocationCardComponent,
    NotificationCardComponent,
    NotesCardComponent,
    TimelineCardComponent,
    RelatedClaimsCardV2Component,
    WcClaimCardComponent,
    AuClaimCardComponent,
    DsClaimCardComponent,
    GlClaimCardComponent,
    LaClaimCardComponent,
    PrClaimCardComponent,
    LeaveBalanceCardComponent,
    LeaveCalendarCardComponent,
    CertificationCardComponent,
    AdClaimCardComponent,
    LinkLabelComponent,
    DeferPanelComponent,
    AnnounceDirective,
  ],
  template: `
    <div
      [appAnnounce]="moveAnnouncement"
      announcePoliteness="assertive"
      class="tw-sr-only"
    ></div>
    @let claim = view.currentClaim();
    @if (claim) {
      <div
        class="tw-mx-auto tw-w-full tw-min-w-0 tw-max-w-full tw-px-0
          xl:tw-px-4"
      >
        @if (hasPendingNewCards && !isMobile()) {
          <div
            class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-w-full tw-mb-3 tw-p-3 tw-rounded-lg tw-bg-[var(--sed-purple-40-color)] app-fade-in"
            role="region"
            aria-label="Layout update available"
          >
            <span class="tw-text-[var(--sed-engaged-black-color)]">
              {{ pendingNewCount === 1 ? 'New card' : 'New cards' }} available
              for this layout: {{ pendingNewCardLabels }}.
              {{ pendingNewCount === 1 ? 'It' : 'They' }} will be added to
              {{ pendingPlacementText }}.
            </span>
            <div class="tw-flex tw-gap-4 tw-items-center">
              <app-link-label
                [ariaLabel]="addPendingAriaLabel"
                (click)="addPendingCards()"
                >Add to layout</app-link-label
              >
              <app-link-label
                ariaLabel="Reset layout to default"
                (click)="resetSettings()"
                >Reset to default</app-link-label
              >
            </div>
          </div>
        }
        @if (showResetButton && !isMobile()) {
          <div class="tw-flex tw-justify-end tw-w-full tw-pb-2 app-fade-in">
            <app-link-label routerLink="" (click)="resetSettings()"
              >Reset layout</app-link-label
            >
          </div>
        } @else {
          <div class="tw-pt-5"></div>
        }
        <div
          class="tw-flex tw-min-w-0 tw-max-w-full tw-flex-col lg:tw-flex-row tw-gap-3"
        >
          <div
            class="tw-w-full lg:tw-basis-1/2 tw-flex tw-min-w-0 tw-max-w-full tw-flex-col
              tw-gap-2 lg:tw-gap-3"
            cdkDropList
            #listOne="cdkDropList"
            [cdkDropListData]="column1"
            (cdkDropListDropped)="drop($event)"
            [cdkDropListConnectedTo]="[listTwo]"
            [cdkDropListDisabled]="isMobile()"
          >
            @for (item of column1; track item.label) {
              @if (this.isSummaryCardAllowed(item)) {
                <app-card
                  [attr.data-card-key]="item.key"
                  [label]="item.label"
                  [showDragDrop]="!isMobile()"
                  labelCase="none"
                  type="Large"
                  [showDragIcon]="!isMobile()"
                  [icon]="
                    item.key === 'ai-summary' ? 'fa-light fa-sparkles' : null
                  "
                  [ariaLabel]="
                    item.label + ' details for claim ' + claim.fileNumber
                  "
                  [moveContext]="
                    isMobile() ? null : getMoveContext(item, column1, $index)
                  "
                  (moveRequest)="
                    onMoveRequest($event.direction, item, column1, $index)
                  "
                  (moveEditModeEnd)="onMoveEditModeEnd()"
                  (moveMenuOpened)="onMoveMenuOpened($event)"
                >
                  <ng-container
                    *ngTemplateOutlet="card; context: { $implicit: item }"
                  ></ng-container>
                </app-card>
              }
            }
          </div>
          <div
            class="tw-w-full lg:tw-basis-1/2 tw-flex tw-min-w-0 tw-max-w-full tw-flex-col
              tw-gap-2 lg:tw-gap-3"
            cdkDropList
            #listTwo="cdkDropList"
            [cdkDropListData]="column2"
            (cdkDropListDropped)="drop($event)"
            [cdkDropListConnectedTo]="[listOne]"
            [cdkDropListDisabled]="isMobile()"
          >
            @for (item of column2; track item.label) {
              @if (this.isSummaryCardAllowed(item)) {
                <app-card
                  [attr.data-card-key]="item.key"
                  [label]="item.label"
                  [showDragDrop]="!isMobile()"
                  labelCase="none"
                  type="Large"
                  [showDragIcon]="!isMobile()"
                  [icon]="
                    item.key === 'ai-summary' ? 'fa-light fa-sparkles' : null
                  "
                  [ariaLabel]="item.label + ' card for ' + claim.fileNumber"
                  [moveContext]="
                    isMobile() ? null : getMoveContext(item, column2, $index)
                  "
                  (moveRequest)="
                    onMoveRequest($event.direction, item, column2, $index)
                  "
                  (moveEditModeEnd)="onMoveEditModeEnd()"
                  (moveMenuOpened)="onMoveMenuOpened($event)"
                >
                  <ng-container
                    *ngTemplateOutlet="card; context: { $implicit: item }"
                  ></ng-container>
                </app-card>
              }
            }
          </div>
        </div>

        <ng-template #card let-item>
          @switch (item.key) {
            @case ('wc-claim') {
              <app-wc-claim-card />
            }
            @case ('gl-claim') {
              <app-gl-claim-card />
            }
            @case ('pr-claim') {
              <app-pr-claim-card />
            }
            @case ('au-claim') {
              <app-au-claim-card />
            }
            @case ('ds-claim') {
              <app-ds-claim-card />
            }
            @case ('la-claim') {
              <app-la-claim-card />
            }
            @case ('ad-claim') {
              <app-ad-claim-card />
            }
            @case ('ai-summary') {
              <app-ai-summary-card />
            }
            @case ('claimant') {
              @defer (on viewport) {
                <app-claimant-card [autoDetail]="autoDetail" />
              } @placeholder {
                <app-defer-panel height="276px" />
              }
            }
            @case ('financials') {
              @defer (on viewport) {
                <app-financial-card />
              } @placeholder {
                <app-defer-panel height="300px" />
              }
            }
            @case ('reserves') {
              @defer (on viewport) {
                <app-reserve-card />
              } @placeholder {
                <app-defer-panel height="300px" />
              }
            }
            @case ('contacts') {
              @defer (on viewport) {
                <app-contact-card
                  [autoDetail]="autoDetail"
                  [glDetail]="glDetail"
                />
              } @placeholder {
                <app-defer-panel height="554px" />
              }
            }

            @case ('related-claims-v2') {
              @defer (on viewport) {
                <app-related-claims-card-v2 />
              } @placeholder {
                <app-defer-panel height="148px" />
              }
            }
            @case ('loss-location') {
              @defer (on viewport) {
                <app-loss-location-card [autoDetail]="autoDetail" />
              } @placeholder {
                <app-defer-panel height="300px" />
              }
            }
            @case ('notifications') {
              @defer (on viewport) {
                <app-notification-card />
              } @placeholder {
                <app-defer-panel height="84px" />
              }
            }
            @case ('notes') {
              @defer (on viewport) {
                <app-notes-card />
              } @placeholder {
                <app-defer-panel height="582px" />
              }
            }
            @case ('timeline') {
              @defer (on viewport) {
                <app-timeline-card />
              } @placeholder {
                <app-defer-panel height="330px" />
              }
            }
            @case ('leave-balance') {
              @defer (on viewport) {
                <app-leave-balance-card />
              } @placeholder {
                <app-defer-panel height="884px" />
              }
            }
            @case ('leave-calendar') {
              @defer (on viewport) {
                <app-leave-calendar-card />
              } @placeholder {
                <app-defer-panel height="370px" />
              }
            }
            @case ('certification') {
              @defer (on viewport) {
                <app-certification-card />
              } @placeholder {
                <app-defer-panel height="272px" />
              }
            }
          }
        </ng-template>
      </div>
    }
  `,
  styles: ``,
})
export class ClaimSummaryComponent extends BaseComponent implements OnInit {
  private settings = inject(SettingsService);
  private injector = inject(Injector);
  private liveAnnouncer = inject(LiveAnnouncer);

  protected isMobile = this.breakpoint.isMobile;

  column1: Action[] = [];
  detailAuth = false;
  column2: Action[] = [];
  notesAuth = false;
  mtTxLawAuth = false;
  custDispAuth = false;
  config: ChartConfig;

  lob: string;
  isLeaveLink = false;
  moveAnnouncement = '';
  showResetButton = false;
  autoDetail: AutoDetail;
  glDetail: GeneralLiabilityResponse;

  /** Authorized default cards added after the user saved this layout, pending the user's choice to add. */
  pendingNewCol1: Action[] = [];
  pendingNewCol2: Action[] = [];

  get hasPendingNewCards(): boolean {
    return this.pendingNewCol1.length > 0 || this.pendingNewCol2.length > 0;
  }

  get pendingNewCount(): number {
    return this.pendingNewCol1.length + this.pendingNewCol2.length;
  }

  get pendingNewCardLabels(): string {
    return [...this.pendingNewCol1, ...this.pendingNewCol2]
      .map((a) => a.label)
      .join(', ');
  }

  /** Human-readable placement (columns only) so the user knows where the cards will land. */
  get pendingPlacementText(): string {
    const cols: string[] = [];
    if (this.pendingNewCol1.length) cols.push('left');
    if (this.pendingNewCol2.length) cols.push('right');
    return cols.length === 2
      ? 'the bottom of the left and right columns'
      : `the bottom of the ${cols[0]} column`;
  }

  get addPendingAriaLabel(): string {
    return `Add ${this.pendingNewCardLabels} to ${this.pendingPlacementText}`;
  }

  /** Append the pending new cards to the user's layout, persist, announce, and focus the new card. */
  addPendingCards(): void {
    const added = [...this.pendingNewCol1, ...this.pendingNewCol2];
    const firstKey = added[0]?.key;
    this.column1 = [...this.column1, ...this.pendingNewCol1];
    this.column2 = [...this.column2, ...this.pendingNewCol2];
    this.pendingNewCol1 = [];
    this.pendingNewCol2 = [];
    this.saveSettings(this.view.currentClaim().lineOfBusiness);
    this.liveAnnouncer.announce(
      `${added.map((a) => a.label).join(', ')} added to your layout.`,
      'polite',
    );
    if (firstKey) {
      this.focusCard(firstKey);
    }
  }

  /** Move keyboard focus to a card host after it renders (parity with card move focus handling). */
  private focusCard(cardKey: string): void {
    afterNextRender(
      () => {
        const host = document.querySelector<HTMLElement>(
          `app-card[data-card-key="${cardKey}"]`,
        );
        if (!host) return;
        host.setAttribute('tabindex', '-1');
        host.focus();
      },
      { injector: this.injector },
    );
  }

  isSummaryCardAllowed(item: Action): boolean {
    switch (item.key) {
      case 'claimant':
        return this.lob === 'LA' || this.lob === 'LV' || this.detailAuth;
      case 'related-claims-v2':
        return this.detailAuth && !this.isLeaveLink;
      case 'notes':
        return this.notesAuth;
      case 'financials':
      case 'reserves':
        return this.session.authorize('ClaimSummaryIncurred');
      case 'ai-summary':
        return (
          this.session.authorize('ClaimSummary') &&
          this.session.authorize('ClaimSummaryIncurred') &&
          this.detailAuth &&
          (this.lob !== 'WC' || !this.mtTxLawAuth)
        );
      default:
        return true;
    }
  }

  async ngOnInit(): Promise<void> {
    // claim summary defers loading cards until in viewport
    // so it's important to load page from top when opening
    // from a claim label link. Claim label assigns the following
    // top parameter.
    const params = getUrlParams();
    if (params.top) {
      window.scrollTo(0, 0);
    }

    const claim = this.view.currentClaim();

    this.lob = claim.lineOfBusiness;
    this.isLeaveLink = leaveLink(claim.recordSource);

    // Load AU-specific data once for all child components
    if (this.lob === 'AU') {
      const claimKey = getClaimKey();
      if (claimKey) {
        this.autoDetail = await this.api.auto.getDetails(claimKey);
      }
    }

    if (this.lob === 'GL' || this.lob === 'PR') {
      const claimKey = getClaimKey();
      if (claimKey) {
        this.glDetail = await this.api.generalLiability.get(claimKey);
      }
    }
    // Initialize authorization before initSettings() so registerX() methods can use it
    this.notesAuth = this.view.authorize('Notes');
    this.detailAuth = this.session.authorize('Details');
    this.mtTxLawAuth = this.view.authorize('MTTXLaw');
    this.custDispAuth = this.view.authorize('CustDisp');
    await this.initSettings();

    this.view.hideLoading();

    this.announcePendingCardsAfterRender();
  }

  /**
   * Announce the "new cards available" notice after the view renders. Doing this
   * during ngOnInit (before render / during route load) causes screen readers to
   * drop the polite update, so defer to the next render and clear() to force
   * re-announcement of an identical message on subsequent loads.
   */
  private announcePendingCardsAfterRender(): void {
    if (!this.hasPendingNewCards) return;
    afterNextRender(
      () => {
        const noun = this.pendingNewCount === 1 ? 'New card' : 'New cards';
        const pronoun = this.pendingNewCount === 1 ? 'it' : 'them';
        this.liveAnnouncer.clear();
        this.liveAnnouncer.announce(
          `${noun} available for this layout: ${this.pendingNewCardLabels}. ` +
            `Use Add to layout to add ${pronoun} to ${this.pendingPlacementText}, or Reset to default.`,
          'polite',
        );
      },
      { injector: this.injector },
    );
  }

  resetSettings() {
    this.column1 = [];
    this.column2 = [];
    this.pendingNewCol1 = [];
    this.pendingNewCol2 = [];
    this.loadDefaultSettings();
    this.showResetButton = false;
    this.deleteSettings(this.lob);
    this.liveAnnouncer.announce('Layout reset to default.', 'polite');
  }

  loadDefaultSettings() {
    switch (this.lob) {
      case 'DS':
        this.registerDs();
        break;
      case 'LA':
        this.registerLa();
        break;
      case 'AD':
        this.registerAD();
        break;
      case 'AU':
        this.registerAu();
        break;
      case 'GL':
        this.registerGl();
        break;
      case 'PR':
        this.registerPr();
        break;
      default:
        this.registerWc();
    }
  }

  private async initSettings() {
    const settings = await this.loadSettings(this.lob);
    if (settings && settings !== '{}') {
      const json = JSON.parse(settings);

      // Check if saved settings have correct claim card for this LOB (may be in column1 or column2 after drag)
      const hasCorrectClaimCard = this.validateClaimCardForLob(
        json.column1,
        json.column2,
      );
      if (hasCorrectClaimCard) {
        const savedCol1: Action[] = json.column1 ?? [];
        const savedCol2: Action[] = json.column2 ?? [];

        // Detect authorized default cards added after this layout was saved
        // (e.g. related-claims-v2), but do NOT mutate the saved layout. Surface
        // them via an inline notice so the user can choose to add them or reset
        // to the default. Card removal is not a feature, so any default card
        // absent from the saved layout is a newly-added/authorized card.
        this.column1 = [];
        this.column2 = [];
        this.loadDefaultSettings();
        const defaultCol1 = this.column1;
        const defaultCol2 = this.column2;

        const savedKeys = new Set(
          [...savedCol1, ...savedCol2].map((a) => a.key),
        );
        this.pendingNewCol1 = defaultCol1.filter((a) => !savedKeys.has(a.key));
        this.pendingNewCol2 = defaultCol2.filter((a) => !savedKeys.has(a.key));

        this.column1 = savedCol1;
        this.column2 = savedCol2;
        this.showResetButton = true;
      } else {
        // Settings are outdated, load fresh defaults
        this.loadDefaultSettings();
        this.showResetButton = false;
        this.deleteSettings(this.lob);
      }
    } else {
      this.loadDefaultSettings();
      this.showResetButton = false;
    }
    this.view.hideLoading();
  }

  private validateClaimCardForLob(
    column1: Action[],
    column2: Action[],
  ): boolean {
    if (!column1 || column1.length === 0) return false;

    const expectedClaimCards: Record<string, string> = {
      WC: 'wc-claim',
      GL: 'gl-claim',
      PR: 'pr-claim',
      AU: 'au-claim',
      DS: 'ds-claim',
      LA: 'la-claim',
      AD: 'ad-claim',
    };

    const hasCard = (column: Action[], cardKey: string): boolean =>
      column?.some((action) => action.key === cardKey) ?? false;

    const expectedCard = expectedClaimCards[this.lob];
    if (!expectedCard) return true; // Unknown LOB, keep settings

    // Claim card may be in column1 or column2 after user drag; require it in at least one
    const hasInColumn1 = hasCard(column1, expectedCard);
    const hasInColumn2 = hasCard(column2, expectedCard);
    if (!hasInColumn1 && !hasInColumn2) return false;

    return true;
  }

  private registerWc() {
    this.register(this.column1, 'wc-claim', 'Claim');
    if (this.session.authorize('Details')) {
      this.register(this.column1, 'claimant', this.view.claimantLabel);
    }
    this.register(this.column1, 'loss-location', 'Loss location');
    if (this.session.authorize('ClaimSummaryIncurred')) {
      this.register(this.column1, 'financials', 'Financials');
      this.register(this.column1, 'reserves', 'Reserves');
    }
    this.register(this.column1, 'contacts', 'Contacts');
    if (
      this.session.authorize('ClaimSummary') &&
      this.session.authorize('ClaimSummaryIncurred') &&
      !this.mtTxLawAuth &&
      this.detailAuth
    ) {
      this.register(this.column2, 'ai-summary', 'Omni AI Summary');
    }
    this.register(this.column2, 'notifications', 'Notifications');
    if (this.notesAuth) {
      this.register(this.column2, 'notes', 'Notes');
    }
    this.register(this.column2, 'timeline', 'Timeline');
    if (this.session.authorize('Property')) {
      this.register(this.column2, 'related-claims-v2', 'Related claims');
    }
  }

  private registerDs() {
    this.register(this.column1, 'ds-claim', 'Claim');
    if (this.session.authorize('Details')) {
      this.register(this.column1, 'claimant', this.view.claimantLabel);
      this.register(this.column2, 'related-claims-v2', 'Related claims');
    }
    this.register(this.column1, 'contacts', 'Contacts');
    this.register(this.column2, 'notifications', 'Notifications');
    if (this.notesAuth && this.custDispAuth) {
      this.register(this.column2, 'notes', 'Notes');
    }
    this.register(this.column2, 'timeline', 'Timeline');
  }

  private registerLa() {
    const claim = this.view.currentClaim();
    const notesAllowed =
      this.notesAuth && (leaveLink(claim?.recordSource) || this.custDispAuth);

    this.register(this.column1, 'la-claim', 'Claim');
    this.register(this.column1, 'certification', 'Certification');
    this.register(this.column1, 'claimant', this.view.claimantLabel);
    this.register(this.column1, 'leave-balance', 'Leave balance');
    this.register(this.column1, 'contacts', 'Contacts');
    if (!this.isLeaveLink) {
      this.register(this.column2, 'related-claims-v2', 'Related claims');
    }
    this.register(this.column2, 'leave-calendar', 'Leave calendar');
    this.register(this.column2, 'notifications', 'Notifications');
    if (notesAllowed) {
      this.register(this.column2, 'notes', 'Notes');
    }
    this.register(this.column2, 'timeline', 'Timeline');
  }

  private registerAD() {
    this.register(this.column1, 'ad-claim', 'Accommodation request');
    if (this.detailAuth) {
      this.register(this.column1, 'claimant', this.view.claimantLabel);
    }
    this.register(this.column1, 'contacts', 'Contacts');
    this.register(this.column2, 'related-claims-v2', 'Related claims');
    if (this.notesAuth) {
      this.register(this.column2, 'notes', 'Notes');
    }
    this.register(this.column2, 'timeline', 'Timeline');
  }

  private registerGl() {
    this.register(this.column1, 'gl-claim', 'Claim');
    if (this.detailAuth) {
      this.register(this.column1, 'claimant', this.view.claimantLabel);
    }
    this.register(this.column1, 'loss-location', 'Loss location');
    if (this.session.authorize('ClaimSummaryIncurred')) {
      this.register(this.column1, 'financials', 'Financials');
      this.register(this.column1, 'reserves', 'Reserves');
    }
    this.register(this.column1, 'contacts', 'Contacts');
    if (
      this.session.authorize('ClaimSummary') &&
      this.session.authorize('ClaimSummaryIncurred') &&
      this.detailAuth
    ) {
      this.register(this.column2, 'ai-summary', 'Omni AI Summary');
    }
    this.register(this.column2, 'notifications', 'Notifications');
    if (this.notesAuth) {
      this.register(this.column2, 'notes', 'Notes');
    }
    this.register(this.column2, 'timeline', 'Timeline');
    if (this.detailAuth) {
      this.register(this.column2, 'related-claims-v2', 'Related claims');
    }
  }

  private registerPr() {
    this.register(this.column1, 'pr-claim', 'Claim');
    if (this.detailAuth) {
      this.register(this.column1, 'claimant', this.view.claimantLabel);
    }
    this.register(this.column1, 'loss-location', 'Loss location');
    if (this.session.authorize('ClaimSummaryIncurred')) {
      this.register(this.column1, 'financials', 'Financials');
      this.register(this.column1, 'reserves', 'Reserves');
    }
    this.register(this.column1, 'contacts', 'Contacts');
    if (
      this.session.authorize('ClaimSummary') &&
      this.session.authorize('ClaimSummaryIncurred') &&
      this.detailAuth
    ) {
      this.register(this.column2, 'ai-summary', 'Omni AI Summary');
    }
    this.register(this.column2, 'notifications', 'Notifications');
    if (this.notesAuth) {
      this.register(this.column2, 'notes', 'Notes');
    }
    this.register(this.column2, 'timeline', 'Timeline');
    if (this.detailAuth) {
      this.register(this.column2, 'related-claims-v2', 'Related claims');
    }
  }

  private registerAu() {
    this.register(this.column1, 'au-claim', 'Claim');
    if (this.detailAuth) {
      this.register(this.column1, 'claimant', this.view.claimantLabel);
    }
    this.register(this.column1, 'loss-location', 'Loss location');
    if (this.session.authorize('ClaimSummaryIncurred')) {
      this.register(this.column1, 'financials', 'Financials');
      this.register(this.column1, 'reserves', 'Reserves');
    }
    this.register(this.column1, 'contacts', 'Contacts');
    if (
      this.session.authorize('ClaimSummary') &&
      this.session.authorize('ClaimSummaryIncurred') &&
      this.detailAuth
    ) {
      this.register(this.column2, 'ai-summary', 'Omni AI Summary');
    }
    this.register(this.column2, 'notifications', 'Notifications');
    if (this.notesAuth) {
      this.register(this.column2, 'notes', 'Notes');
    }
    this.register(this.column2, 'timeline', 'Timeline');
    if (this.session.authorize('Details')) {
      this.register(this.column2, 'related-claims-v2', 'Related claims');
    }
  }

  private register(column: Action[], key: string, label: string) {
    column.push({
      key,
      label,
    });
  }

  goBack(): void {
    this.view.exitClaim();
  }

  drop(event: CdkDragDrop<any>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
    this.saveSettings(this.view.currentClaim().lineOfBusiness);
  }

  moveUp(item: Action, column: Action[], index: number): void {
    if (index <= 0) return;
    moveItemInArray(column, index, index - 1);
    const newIndex = index - 1;
    const newPos = newIndex + 1;
    const total = column.length;
    const colName = column === this.column1 ? 'left' : 'right';
    this.moveAnnouncement = `${item.label} moved up. Position ${newPos} of ${total} in ${colName} column.${this.getBoundaryText(newIndex, total)}`;
    this.saveSettings(this.view.currentClaim().lineOfBusiness);
  }

  moveDown(item: Action, column: Action[], index: number): void {
    if (index >= column.length - 1) return;
    moveItemInArray(column, index, index + 1);
    const newIndex = index + 1;
    const newPos = newIndex + 1;
    const total = column.length;
    const colName = column === this.column1 ? 'left' : 'right';
    this.moveAnnouncement = `${item.label} moved down. Position ${newPos} of ${total} in ${colName} column.${this.getBoundaryText(newIndex, total)}`;
    this.saveSettings(this.view.currentClaim().lineOfBusiness);
  }

  moveLeft(item: Action, column: Action[], index: number): void {
    if (column !== this.column2) return;
    const fromIndex = this.column2.indexOf(item);
    if (fromIndex === -1) return;
    const toIndex = Math.min(fromIndex, this.column1.length);
    transferArrayItem(this.column2, this.column1, fromIndex, toIndex);
    const newPos = toIndex + 1;
    const total = this.column1.length;
    this.moveAnnouncement = `${item.label} moved to left column. Position ${newPos} of ${total} in left column.${this.getBoundaryText(toIndex, total)}`;
    this.saveSettings(this.view.currentClaim().lineOfBusiness);
    this.reopenMoveMenuOnNewCard(item.key);
  }

  moveRight(item: Action, column: Action[], index: number): void {
    if (column !== this.column1) return;
    const fromIndex = this.column1.indexOf(item);
    if (fromIndex === -1) return;
    const toIndex = Math.min(fromIndex, this.column2.length);
    transferArrayItem(this.column1, this.column2, fromIndex, toIndex);
    const newPos = toIndex + 1;
    const total = this.column2.length;
    this.moveAnnouncement = `${item.label} moved to right column. Position ${newPos} of ${total} in right column.${this.getBoundaryText(toIndex, total)}`;
    this.saveSettings(this.view.currentClaim().lineOfBusiness);
    this.reopenMoveMenuOnNewCard(item.key);
  }

  private getBoundaryText(newIndex: number, columnLength: number): string {
    const parts: string[] = [];
    if (newIndex === 0) parts.push('Top of column.');
    if (newIndex === columnLength - 1) parts.push('Bottom of column.');
    return parts.length ? ' ' + parts.join(' ') : '';
  }

  /**
   * Cross-column moves destroy the source card+menu, so re-open the menu on
   * the destination card once Angular has rendered it (parity with Move Up/Down).
   */
  private reopenMoveMenuOnNewCard(cardKey: string): void {
    afterNextRender(
      () => {
        const host = document.querySelector<HTMLElement>(
          `app-card[data-card-key="${cardKey}"]`,
        );
        if (!host) return;
        const trigger = host.querySelector<HTMLElement>(
          '[data-move-trigger] button',
        );
        if (!trigger) return;
        trigger.focus();
        trigger.click();
      },
      { injector: this.injector },
    );
  }

  getMoveContext(
    item: Action,
    column: Action[],
    index: number,
  ): {
    canMoveUp: boolean;
    canMoveDown: boolean;
    canMoveLeft: boolean;
    canMoveRight: boolean;
  } {
    const isColumn1 = column === this.column1;
    return {
      canMoveUp: index > 0,
      canMoveDown: index < column.length - 1,
      canMoveLeft: !isColumn1,
      canMoveRight: isColumn1,
    };
  }

  onMoveRequest(
    direction: 'up' | 'down' | 'left' | 'right',
    item: Action,
    column: Action[],
    index: number,
  ): void {
    switch (direction) {
      case 'up':
        this.moveUp(item, column, index);
        break;
      case 'down':
        this.moveDown(item, column, index);
        break;
      case 'left':
        this.moveLeft(item, column, index);
        break;
      case 'right':
        this.moveRight(item, column, index);
        break;
    }
  }

  /** Called when move edit mode ends (Escape, click outside, or 2s inactivity). Save layout once. */
  onMoveEditModeEnd(): void {
    this.saveSettings(this.view.currentClaim().lineOfBusiness);
  }

  onMoveMenuOpened(cardLabel: string): void {
    this.moveAnnouncement = `Move menu for ${cardLabel} opened. Move up, down, left, or right. Or use Shift and Arrow keys.`;
  }

  private async loadSettings(lob: string) {
    const settings = await this.settings.get(`claim-summary-columns-${lob}`);
    return settings;
  }

  private async saveSettings(lob: string) {
    const json = JSON.stringify({
      column1: this.column1,
      column2: this.column2,
    });
    await this.settings.set(`claim-summary-columns-${lob}`, json);
    this.showResetButton = true;
  }

  private deleteSettings(lob: string) {
    this.settings.set(`claim-summary-columns-${lob}`, '{}');
  }
}
